import React from "react";
import { Combobox, Label, makeStyles, mergeClasses, Option, Skeleton, SkeletonItem } from "@fluentui/react-components";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import useSearchableCombobox, { SearchableComboboxOption } from "./SearchableComboboxHook";

export type { SearchableComboboxOption };

// ── Styles ─────────────────────────────────────────────────────────────────

// Disabled palette aligned with Fluent v8 Dropdown (used by the Form field):
// no visible border, light grey fill, and muted text for both label and placeholder.
const DISABLED_BG_COLOR = "rgb(243, 242, 241)"; // #F3F2F1
const DISABLED_TEXT_COLOR = "rgb(161, 159, 157)"; // #A19F9D

const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "block",
    width: "100%",
  },
  inputWrapper: {
    position: "relative",
    display: "block",
  },
  // Single loading row shown in place of the combobox while data is being fetched.
  // Matches the combobox input height so the layout doesn't shift once it resolves.
  skeletonRow: {
    height: "25px",
    width: "100%",
    borderRadius: "2px",
    marginBottom: "10",
  },
  noResultText: {
    color: "var(--color-blue)",
    textAlign: "center",
    display: "block",
    width: "100%",
  },
  errorMessage: {
    color: "var(--color-red)",
    fontSize: "12px",
    marginTop: "4px",
    display: "block",
  },
  // Replicate the Fluent v8 Dropdown label spacing ("padding: 5px 0", used by the
  // Form field) so the gap between the label and the combobox matches the Form field.
  label: {
    display: "block",
    paddingTop: "5px",
    paddingBottom: "5px",
  },
  // Restore horizontal padding so the typed/selected text is not flush against the
  // wrapper border (matches the Fluent v8 Dropdown padding used elsewhere, e.g. Form).
  inputSlot: {
    paddingLeft: "8px",
    paddingRight: "8px",
  },
  // Cap visible rows to 10 with internal scroll (FR-006, F8). Rows are 32px tall with
  // a 4px gap (option marginBottom), so ~10 rows fit in 364px (8 padding + 10×32 + 9×4).
  // Opaque background + shadow so the panel stays readable when it overlays content below.
  listbox: {
    maxHeight: "364px",
    overflowY: "auto",
    overflowX: "hidden",
    backgroundColor: "var(--color-white, #ffffff)",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.14)",
    zIndex: 1000,
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  // Lighter neutral for the trailing icons (chevron / clear ×) to match the colour
  // used by the Application dropdown elsewhere in the add-in.
  trailingIcon: {
    color: "rgb(161, 159, 157)",
  },
  // Per-option spacing: roomier fixed row height with horizontal padding so the text
  // isn't cramped. flexShrink: 0 is CRITICAL — the Fluent listbox is a flex column, so
  // without it the options shrink to fit the maxHeight when there are many of them
  // (e.g. Excel sources), making the rows look tiny. Keeping them rigid makes the
  // listbox scroll instead. Fixed height keeps the 10-row cap exact; wordBreak lets
  // long labels wrap instead of forcing a horizontal scroll.
  option: {
    boxSizing: "border-box",
    height: "32px",
    flexShrink: 0,
    marginBottom: "4px",
    paddingLeft: "12px",
    paddingRight: "12px",
    display: "flex",
    alignItems: "center",
    wordBreak: "break-word",
  },
  // Highlight the currently-selected option with a neutral grey background.
  optionSelected: {
    backgroundColor: "rgb(237, 235, 233)",
  },
  // Highlight the keyboard-active option with a slightly stronger grey so the user
  // can clearly see where the arrow keys have moved focus.
  optionActive: {
    backgroundColor: "rgb(225, 223, 221)",
  },
});

// ── Public API ─────────────────────────────────────────────────────────────

export interface SearchableComboboxProps {
  options: SearchableComboboxOption[];
  selectedKey?: string;
  /** Called on valid selection OR clear. Passes (undefined, undefined) when cleared. */
  onOptionSelect: (key?: string, text?: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  /** Forwarded to the inner Combobox input element — preserves E2E selectors (e.g. sheet_dropdown). */
  id?: string;
  errorMessage?: string;
  /** Forwarded as data-testid to the inner Combobox input element. */
  dataTestId?: string;
}

// ── Component ──────────────────────────────────────────────────────────────

export const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
  options,
  selectedKey,
  onOptionSelect,
  placeholder,
  label,
  disabled = false,
  loading = false,
  required,
  id,
  errorMessage,
  dataTestId,
}) => {
  const styles = useStyles();

  const {
    open,
    comboValue,
    filteredOptions,
    activeIndex,
    optionRefs,
    isError,
    isDisabled,
    isFreeform,
    handleOpenChange,
    handleFluentOptionSelect,
    handleInputChangeCapture,
    handleInputKeyDownCapture,
  } = useSearchableCombobox({ options, selectedKey, onOptionSelect, disabled, loading, errorMessage });

  // ── Render ───────────────────────────────────────────────────────────────

  // Build the input slot props as a variable rather than an inline object literal.
  // TypeScript's excess-property check fires only on fresh object literals; assigning
  // via a variable uses structural compatibility checks instead, which allows
  // "data-testid" (not in InputHTMLAttributes) to flow through to the DOM. (F4 — id)
  const inputSlotProps = {
    id,
    required,
    className: styles.inputSlot,
    // Single-option and zero-option: input is read-only, no freeform typing (FR-012)
    readOnly: !isFreeform,
    // Forwarded as data-testid to enable test/E2E selectors without adding a separate prop
    "data-testid": dataTestId,
    // aria-invalid on the input so getByRole("combobox") exposes it (FR-009)
    "aria-invalid": isError ? ("true" as const) : undefined,
    // Capture-phase handlers — fire BEFORE Fluent's bubble-phase merged handlers
    onChangeCapture: handleInputChangeCapture,
    onKeyDownCapture: handleInputKeyDownCapture,
  };

  // Normalize the label so it always ends with ":" (caption package is inconsistent
  // across "sheet" vs "chart" keys). Keep the rendered text aligned with the
  // PowerPoint add-in convention (e.g. "Sheet: *", "Chart: *"). Trailing space
  // ensures a visual gap between ":" and the required asterisk.
  const normalizedLabel = label
    ? label.trimEnd().endsWith(":")
      ? `${label.trimEnd()} `
      : `${label.trimEnd()}: `
    : undefined;

  // Render the asterisk in the same dark red as Fluent v8 Dropdown (used by the
  // Form field elsewhere in the panel). This keeps required indicators visually
  // consistent across the two component generations.
  const requiredMarker = required ? <span style={{ color: "rgb(164, 38, 44)" }}>*</span> : undefined;

  // Muted label text when the field is disabled, to match the Form field.
  const labelStyle: React.CSSProperties | undefined = isDisabled ? { color: DISABLED_TEXT_COLOR } : undefined;

  const comboboxStyle: React.CSSProperties = {
    width: "100%",
    border: isError ? "1px solid var(--color-red)" : isDisabled ? "1px solid transparent" : "1px solid rgb(96, 94, 92)",
    backgroundColor: isDisabled ? DISABLED_BG_COLOR : undefined,
    color: isDisabled ? DISABLED_TEXT_COLOR : undefined,
  };

  return (
    <div className={styles.root}>
      {normalizedLabel && (
        <Label required={requiredMarker} className={styles.label} style={labelStyle}>
          {normalizedLabel}
        </Label>
      )}
      {loading ? (
        // Single loading row shown while data is being fetched (replaces the
        // combobox so the field reads as "loading" rather than "disabled").
        <Skeleton aria-label={placeholder}>
          <SkeletonItem className={styles.skeletonRow} animation={"pulse"} />
        </Skeleton>
      ) : (
        <div className={styles.inputWrapper}>
          <Combobox
            style={comboboxStyle}
            value={comboValue}
            selectedOptions={selectedKey ? [selectedKey] : []}
            open={open}
            onOpenChange={handleOpenChange}
            onOptionSelect={handleFluentOptionSelect}
            clearable
            freeform={isFreeform}
            disabled={isDisabled}
            placeholder={placeholder}
            // Suppress auto-flip AND autoSize: keep the panel anchored below the
            // input and stop Fluent's positioning from writing its own
            // viewport-based maxHeight onto the listbox (which would override our
            // fixed maxHeight and show more than 10 rows). (FR-002, F11)
            positioning={{ position: "below", align: "start", fallbackPositions: [], autoSize: false }}
            listbox={{ className: styles.listbox }}
            expandIcon={{ className: styles.trailingIcon }}
            clearIcon={{ className: styles.trailingIcon }}
            input={inputSlotProps}
          >
            {filteredOptions.length === 0 ? (
              // Non-interactive "No result(s) found" row (FR-007)
              <Option disabled aria-disabled="true" value="" text="" checkIcon={null} className={styles.option}>
                <span className={styles.noResultText}>{formatMessage("cof.base.noresultfound")}</span>
              </Option>
            ) : (
              filteredOptions.map((o, idx) => {
                const isSelected = o.key === selectedKey;
                const isActive = idx === activeIndex;
                const optionClassName = mergeClasses(
                  styles.option,
                  isActive ? styles.optionActive : isSelected ? styles.optionSelected : undefined
                );
                return (
                  <Option
                    key={o.key}
                    value={o.key}
                    text={o.text}
                    // Drop the default check icon — selection is conveyed by the
                    // grey background instead.
                    checkIcon={null}
                    className={optionClassName}
                    ref={(el: HTMLDivElement | null) => {
                      optionRefs.current[idx] = el;
                    }}
                  >
                    {o.text}
                  </Option>
                );
              })
            )}
          </Combobox>
        </div>
      )}

      {/* External error message rendered below the input (§4) */}
      {errorMessage && <span className={styles.errorMessage}>{errorMessage}</span>}
    </div>
  );
};
